import { useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import type { ReviewQueueItem } from "@/components/building-data/BuildingDataReviewQueue";
import { isAllowedEnumValue } from "@/components/building-data/enumValue";
import { getEnumRule } from "@/components/building-data/getEnumRule";
import { stripTrailingUnit } from "@/components/building-data/stripTrailingUnit";
import { validateFieldValue } from "@/components/building-data/validateFieldValue";
import { useDocumentPreview } from "@/components/chat/SidebarChat/DocLink/useDocumentPreview";
import type { WidgetField, WidgetGroup } from "@/domains/buildings/types/buildingWidget";
import { useBuildingDetail } from "@/hooks/buildings/useBuildingDetail";
import { useBuildingWidgetData } from "@/hooks/buildings/useBuildingWidgetData";
import { useSaveFieldValue } from "@/hooks/buildings/useSaveFieldValue";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useStores } from "@/stores";
import {
  BuildingDataPageLayout,
  type BuildingDataPageLayoutProps,
} from "./building-data/BuildingDataPageLayout";
import type { StateFilter } from "./building-data/BuildingDataPageHeader";
import {
  useBuildingDataPageEffects,
  useBuildingDataPageState,
} from "./building-data/useBuildingDataPageEffects";

function filterGroups(
  groups: WidgetGroup[],
  query: string,
  activeFilter: StateFilter
): WidgetGroup[] {
  const needle = query.trim().toLowerCase();
  return groups
    .map((group) => {
      const fields = group.fields.filter((field) => {
        if (activeFilter !== "all" && field.state !== activeFilter) return false;
        if (!needle) return true;
        return (
          field.label.toLowerCase().includes(needle) || field.value.toLowerCase().includes(needle)
        );
      });
      return { ...group, fields };
    })
    .filter((group) => group.fields.length > 0);
}

function availableGroups(isError: boolean, groups: WidgetGroup[] | undefined): WidgetGroup[] {
  if (isError) return [];
  return groups ?? [];
}

function buildingIdForView(canView: boolean, buildingId: string): string | undefined {
  return canView ? buildingId : undefined;
}

function routeBuildingId(id: string | undefined): string {
  return id ?? "";
}

function organizationId(organization: { id: string } | null | undefined): string | undefined {
  return organization?.id;
}

function canViewBuildingData(
  buildingId: string,
  permissions: ReturnType<typeof usePermissions>
): boolean {
  if (!buildingId) return false;
  return permissions.canViewBuildingDataSidebar(Number(buildingId));
}

function widgetGroups(widgetQuery: ReturnType<typeof useBuildingWidgetData>): WidgetGroup[] {
  return availableGroups(widgetQuery.isError, widgetQuery.data?.groups);
}

function registryCode(detail: ReturnType<typeof useBuildingDetail>["detail"]): string | null {
  return detail?.base.registry_code ?? null;
}

function previewBuildingId(buildingId: string | undefined): string {
  return buildingId ?? "";
}

interface BuildingDataRouteParams {
  id: string;
}

function useBuildingDataResources() {
  const { id } = useParams<keyof BuildingDataRouteParams>();
  const buildingId = routeBuildingId(id);
  const perms = usePermissions();
  const { currentOrganization } = useOrganizationContext();
  const buildingIdNum = Number(buildingId);
  const orgId = organizationId(currentOrganization);
  const canView = canViewBuildingData(buildingId, perms);
  const gatedBuildingId = buildingIdForView(canView, buildingId);
  const canManage = perms.canManageBuildingDataSidebar(buildingIdNum);
  const { detail } = useBuildingDetail(gatedBuildingId);
  const widgetQuery = useBuildingWidgetData(gatedBuildingId, orgId);
  const groups = useMemo(() => widgetGroups(widgetQuery), [widgetQuery]);
  return {
    buildingId,
    orgId,
    canView,
    canManage,
    registryCode: registryCode(detail),
    widgetQuery,
    groups,
    documentPreview: useDocumentPreview(previewBuildingId(gatedBuildingId)),
    saveMutation: useSaveFieldValue(buildingId, orgId),
  };
}

function useBuildingDataDerived(
  groups: WidgetGroup[],
  query: string,
  activeFilter: StateFilter,
  selectedFieldId: string | null
) {
  const allFields = useMemo(() => groups.flatMap((group) => group.fields), [groups]);
  const filteredGroups = useMemo(
    () => filterGroups(groups, query, activeFilter),
    [activeFilter, groups, query]
  );
  const selectedField = allFields.find((field) => field.id === selectedFieldId) ?? null;
  const selectedGroup =
    groups.find((group) => group.fields.some((field) => field.id === selectedFieldId)) ?? null;
  const reviewItems = useMemo<ReviewQueueItem[]>(
    () =>
      groups.flatMap((group) =>
        group.fields
          .filter((field) => field.state === "pending")
          .map((field) => ({ field, groupTitle: group.title }))
      ),
    [groups]
  );
  return { allFields, filteredGroups, selectedField, selectedGroup, reviewItems };
}

type PageState = ReturnType<typeof useBuildingDataPageState>;
type PageResources = ReturnType<typeof useBuildingDataResources>;
type PageDerived = ReturnType<typeof useBuildingDataDerived>;
type BuildingDataStore = ReturnType<typeof useStores>["buildingDataWidgetStore"];
type Toast = ReturnType<typeof useToast>["toast"];

function preparedConfirmationFields(fields: WidgetField[], language: string) {
  return fields.map((field) => ({
    field,
    value: field.unit ? stripTrailingUnit(field.value) : field.value.trim(),
    isValid: (() => {
      const enumRule = getEnumRule(field.validation, language === "et" ? "et" : "en");
      const value = field.unit ? stripTrailingUnit(field.value) : field.value.trim();
      return enumRule
        ? isAllowedEnumValue(value, enumRule)
        : validateFieldValue(value, field.validation).ok;
    })(),
  }));
}

interface ConfirmationResultInput {
  requested: number;
  valid: number;
  confirmed: number;
  toast: Toast;
  tw: BuildingDataPageLayoutProps["tw"];
}

function notifyConfirmationResult(input: ConfirmationResultInput) {
  const { confirmed, requested, toast, tw, valid } = input;
  if (confirmed === requested) {
    toast({ description: tw.sectionConfirmed.replace("{count}", String(confirmed)) });
  } else if (confirmed === valid) {
    toast({
      title: tw.extremeValueTitle,
      description: tw.sectionConfirmValidationBlocked
        .replace("{confirmed}", String(confirmed))
        .replace("{invalid}", String(requested - valid)),
    });
  } else {
    toast({
      variant: "destructive",
      title: tw.saveError,
      description: tw.sectionConfirmPartial
        .replace("{confirmed}", String(confirmed))
        .replace("{total}", String(requested)),
    });
  }
}

async function saveConfirmationFields(
  fields: ReturnType<typeof preparedConfirmationFields>,
  saveMutation: PageResources["saveMutation"]
): Promise<number> {
  const results = await Promise.allSettled(
    fields.map(({ field, value }) =>
      saveMutation.mutateAsync({
        attributeKey: field.id,
        attributeValue: value,
        previousValue: "",
        offerUndo: false,
        silentError: true,
      })
    )
  );
  return results.filter((result) => result.status === "fulfilled").length;
}

interface ConfirmationHandlerInput {
  language: string;
  saveMutation: PageResources["saveMutation"];
  toast: Toast;
  tw: BuildingDataPageLayoutProps["tw"];
}

async function confirmAllFields(input: ConfirmationHandlerInput, fields: WidgetField[]) {
  const { language, saveMutation, toast, tw } = input;
  const prepared = preparedConfirmationFields(fields, language);
  const valid = prepared.filter((entry) => entry.isValid);
  const confirmed = await saveConfirmationFields(valid, saveMutation);
  notifyConfirmationResult({
    requested: fields.length,
    valid: valid.length,
    confirmed,
    toast,
    tw,
  });
}

interface ConfirmAllFieldsInput {
  language: string;
  resources: PageResources;
  toast: Toast;
  tw: BuildingDataPageLayoutProps["tw"];
}

function useConfirmAllFields(input: ConfirmAllFieldsInput) {
  const { language, resources, toast, tw } = input;
  const saveMutation = resources.saveMutation;
  return useCallback(
    (fields: WidgetField[]) => confirmAllFields({ language, saveMutation, toast, tw }, fields),
    [language, saveMutation, toast, tw]
  );
}

function usePageNavigation(resources: PageResources, state: PageState, store: BuildingDataStore) {
  const {
    contentScrollRef,
    setActiveFilter,
    setActiveSection,
    setInspectorOpen,
    setQuery,
    setSelectedFieldId,
  } = state;
  const onDocPreview = useCallback(
    (filename: string) => void resources.documentPreview.openPreview(filename),
    [resources.documentPreview]
  );
  const onFieldSelect = useCallback(
    (field: WidgetField) => {
      setSelectedFieldId(field.id);
      setInspectorOpen(true);
    },
    [setInspectorOpen, setSelectedFieldId]
  );
  const onSelectGroup = (group: WidgetGroup) => {
    setQuery("");
    setActiveFilter("all");
    store.openGroup(group.id);
    setActiveSection(group.id);
    window.requestAnimationFrame(() => {
      contentScrollRef.current
        ?.querySelector(`[data-bd-section="${CSS.escape(group.id)}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  return { onDocPreview, onFieldSelect, onSelectGroup };
}

interface LayoutPropsInput {
  title: string;
  tw: BuildingDataPageLayoutProps["tw"];
  resources: PageResources;
  derived: PageDerived;
  state: PageState;
  store: BuildingDataStore;
  navigation: ReturnType<typeof usePageNavigation>;
  onConfirmAll: (fields: WidgetField[]) => Promise<void>;
}

function layoutProps(input: LayoutPropsInput): BuildingDataPageLayoutProps {
  const { derived, navigation, onConfirmAll, resources, state, store, title, tw } = input;
  return {
    title,
    tw,
    data: resources.widgetQuery.data,
    isLoading: resources.widgetQuery.isLoading,
    isError: resources.widgetQuery.isError,
    groups: resources.groups,
    filteredGroups: derived.filteredGroups,
    reviewItems: derived.reviewItems,
    selectedField: derived.selectedField,
    selectedGroup: derived.selectedGroup,
    selectedFieldId: state.selectedFieldId,
    inspectorOpen: state.inspectorOpen,
    reviewOpen: state.reviewOpen,
    historyOpen: state.historyOpen,
    activeSection: state.activeSection,
    activeFilter: state.activeFilter,
    query: state.query,
    buildingId: resources.buildingId,
    orgId: resources.orgId,
    registryCode: resources.registryCode,
    canManage: resources.canManage,
    documentPreview: resources.documentPreview,
    historyTriggerRef: state.historyTriggerRef,
    onSearchInputMount: state.mountSearchInput,
    onContentScrollMount: state.mountContentScroll,
    onRetry: () => void resources.widgetQuery.refetch(),
    onFilterChange: state.setActiveFilter,
    onQueryChange: state.setQuery,
    onOpenReview: () => state.setReviewOpen(true),
    onCloseReview: () => state.setReviewOpen(false),
    onOpenHistory: () => state.setHistoryOpen(true),
    onHistoryOpenChange: state.setHistoryOpen,
    onCloseInspector: () => state.setInspectorOpen(false),
    onSelectGroup: navigation.onSelectGroup,
    onDocPreview: navigation.onDocPreview,
    onFieldSelect: navigation.onFieldSelect,
    onConfirmAll,
    openGroupIds: store.openGroupIds.slice(),
    onGroupToggle: (groupId) => store.toggleGroup(groupId),
  };
}

const BuildingDataPage = observer(function BuildingDataPage() {
  const { t, language } = useLanguage();
  const { buildingDataWidgetStore: store } = useStores();
  const { toast } = useToast();
  const tw = t.buildings.buildingDataWidget;
  const resources = useBuildingDataResources();
  const state = useBuildingDataPageState();
  const derived = useBuildingDataDerived(
    resources.groups,
    state.query,
    state.activeFilter,
    state.selectedFieldId
  );
  const navigation = usePageNavigation(resources, state, store);
  const onConfirmAll = useConfirmAllFields({ language, resources, toast, tw });
  useBuildingDataPageEffects({
    resources,
    derived,
    state,
    store,
    navigation,
    toast,
    errorTitle: tw.cannotOpenDocument,
  });

  if (!resources.canView) return null;
  const props = layoutProps({
    title: t.buildings.sidebar.buildingData,
    tw,
    resources,
    derived,
    state,
    store,
    navigation,
    onConfirmAll,
  });
  return <BuildingDataPageLayout {...props} />;
});

export default BuildingDataPage;
